import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Star, ExternalLink, ShoppingCart } from 'lucide-react'
import { RecommendationResponse, RunningShoe } from '../types'
import { getRecommendationResults } from '../utils/session'

const RecommendationsPage = () => {
  const [results, setResults] = useState<RecommendationResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const storedResults = getRecommendationResults()
    
    if (!storedResults) {
      // No results found, redirect to home
      navigate('/')
      return
    }
    
    setResults(storedResults)
    setLoading(false)
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-lg text-secondary-600 font-medium">Loading your perfect matches...</p>
        </div>
      </div>
    )
  }

  if (!results || !results.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center">
        <div className="text-center card shadow-soft-lg max-w-md">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-4">
            No Recommendations Found
          </h2>
          <p className="text-secondary-600 mb-8 leading-relaxed">
            We couldn't find your recommendation results. Please try again.
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary btn-large"
          >
            Start New Search
          </button>
        </div>
      </div>
    )
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
  }

  const calculateSavings = (shoe: RunningShoe) => {
    if (shoe.price.original && shoe.price.original > shoe.price.current) {
      return shoe.price.original - shoe.price.current
    }
    return 0
  }

  const calculateDiscountPercentage = (shoe: RunningShoe) => {
    if (shoe.price.original && shoe.price.original > shoe.price.current) {
      return Math.round(((shoe.price.original - shoe.price.current) / shoe.price.original) * 100)
    }
    return 0
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-semibold mb-6 inline-flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-white hover:shadow-soft transition-all duration-300"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Start New Search</span>
          </button>
          
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-6">
            Your Perfect Running Shoes
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl">
            Based on your profile, here are our top recommendations with the best prices and value
          </p>
        </div>


        {/* Recommendations */}
        <div className="grid gap-8 md:gap-10">
          {(results.recommendations || []).map((shoe, index) => (
            <div key={shoe._id} className="card card-hover shadow-soft-lg">
              <div className="flex items-start space-x-4 mb-8">
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl w-12 h-12 flex items-center justify-center text-lg font-bold shadow-lg">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
                    {shoe.name}
                  </h3>
                  <p className="text-lg text-secondary-600 font-medium">
                    {shoe.brand} • {shoe.model}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Image */}
                <div className="md:col-span-1">
                  <div className="aspect-square bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-2xl overflow-hidden shadow-soft border border-secondary-200">
                    {shoe.images.length > 0 ? (
                      <img
                        src={shoe.images[0]}
                        alt={shoe.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src = `https://via.placeholder.com/300x300/f3f4f6/9ca3af?text=${encodeURIComponent(shoe.brand)}`
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-secondary-400">
                        <ShoppingCart className="h-16 w-16" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="md:col-span-2">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div>
                      <div className="mb-6">
                        <h4 className="font-bold text-secondary-900 mb-3 text-lg">Price</h4>
                        <div className="flex items-baseline space-x-3">
                          <span className="text-3xl font-bold text-accent-600">
                            {formatPrice(shoe.price.current)}
                          </span>
                          {shoe.price.original && shoe.price.original > shoe.price.current && (
                            <>
                              <span className="text-xl text-secondary-500 line-through">
                                {formatPrice(shoe.price.original)}
                              </span>
                              <span className="bg-gradient-to-r from-red-500 to-red-600 text-white font-bold px-3 py-1 rounded-full text-sm">
                                {calculateDiscountPercentage(shoe)}% OFF
                              </span>
                            </>
                          )}
                        </div>
                        {calculateSavings(shoe) > 0 && (
                          <p className="text-accent-600 mt-2 font-semibold">
                            💰 You save {formatPrice(calculateSavings(shoe))}
                          </p>
                        )}
                      </div>

                      <div className="mb-6">
                        <h4 className="font-bold text-secondary-900 mb-3 text-lg">Terrain</h4>
                        <div className="flex flex-wrap gap-2">
                          {shoe.terrain.map((terrain) => (
                            <span
                              key={terrain}
                              className="bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 font-medium px-4 py-2 rounded-full border border-primary-200"
                            >
                              {terrain}
                            </span>
                          ))}
                        </div>
                      </div>

                      {shoe.ratings?.overall && (
                        <div className="mb-6">
                          <h4 className="font-bold text-secondary-900 mb-3 text-lg">Rating</h4>
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-5 w-5 ${
                                    i < Math.floor(shoe.ratings!.overall!)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-secondary-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-secondary-700 font-medium">
                              {shoe.ratings.overall.toFixed(1)}
                              {shoe.reviews?.count && ` (${shoe.reviews.count} reviews)`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div>
                      {(shoe as any).aiReasoning && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Why This Shoe?</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(shoe as any).aiReasoning}
                          </p>
                          {(shoe as any).matchScore && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">
                                Match Score: {(shoe as any).matchScore}/100
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Key Features</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {shoe.features.slice(0, 4).map((feature, idx) => (
                            <li key={idx}>• {feature}</li>
                          ))}
                        </ul>
                      </div>

                      {shoe.specifications && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Specifications</h4>
                          <div className="text-sm text-gray-600 space-y-1">
                            {shoe.specifications.weight && (
                              <div>Weight: {shoe.specifications.weight} oz</div>
                            )}
                            {shoe.specifications.drop && (
                              <div>Drop: {shoe.specifications.drop}mm</div>
                            )}
                            {shoe.specifications.cushioning && (
                              <div>Cushioning: {shoe.specifications.cushioning}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t border-secondary-200">
                    <a
                      href={shoe.availability.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary btn-large flex items-center space-x-3 flex-1 justify-center"
                    >
                      <span>Buy Now at {shoe.availability.retailer}</span>
                      <ExternalLink className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => navigate(`/shoe/${shoe._id}`)}
                      className="btn-outline btn-large flex-1"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-16 p-12 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl border border-primary-200 shadow-soft">
          <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
            Not quite right?
          </h3>
          <p className="text-lg text-secondary-600 mb-8 max-w-2xl mx-auto">
            Try adjusting your preferences or explore more options. Our AI learns from every search to provide better recommendations.
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary btn-large"
          >
            Start New Search
          </button>
        </div>
      </div>
    </div>
  )
}

export default RecommendationsPage